import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AbiShell from "@/pages/abi/AbiShell";
import { AbiAzioni } from "@/pages/abi/AbiAzioni";
import { AbiConfronto } from "@/pages/abi/AbiConfronto";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Building2, Loader2, FileText, ShieldAlert, ListChecks, RefreshCw, Link2, Copy, CheckCircle2, RotateCcw } from "lucide-react";

const SNAP_FIELDS = [
  ["stato_piva", "Stato P.IVA"], ["forma_giuridica", "Forma giuridica"], ["sede", "Sede"],
  ["ateco", "ATECO"], ["settore", "Settore"], ["fatturato", "Fatturato"],
  ["dipendenti", "Dipendenti/Addetti"], ["pec", "PEC"], ["sito", "Sito web"],
];

const ETICHETTA_META = {
  fatto_verificato: { label: "Verificato", cls: "bg-emerald-100 text-emerald-700" },
  dato_dichiarato: { label: "Dichiarato", cls: "bg-blue-100 text-blue-700" },
  inferenza: { label: "Inferenza", cls: "bg-amber-100 text-amber-700" },
};
const LIVELLO_BADGE = { 1: "🟢", 2: "🔵", 3: "🟡", 4: "⚪" };

const fmtSnap = (k, v) => {
  if (v === null || v === undefined || v === "" || !/^\d+(\.\d+)?$/.test(String(v))) return v;
  const n = Number(v).toLocaleString("it-IT", { maximumFractionDigits: 0 });
  return k === "fatturato" ? `€ ${n}` : k === "dipendenti" ? n : v;
};

function Val({ v }) {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0))
    return <span className="text-slate-400 italic">Dato non reperito nelle fonti gratuite consultate</span>;
  if (Array.isArray(v)) return <span className="text-slate-800">{v.join(" · ")}</span>;
  return <span className="text-slate-800">{v}</span>;
}

const etichettaDi = (c) => (c.sourceType === 1 ? "fatto_verificato" : c.etichetta === "inferenza" ? "inferenza" : "dato_dichiarato");

function Campo({ c }) {
  const et = ETICHETTA_META[etichettaDi(c)];
  return (
    <div className="flex items-start justify-between gap-2 text-sm border-b border-slate-50 pb-1.5">
      <span className="text-slate-500 shrink-0">{c.label}</span>
      <span className="text-right">
        <Val v={c.value} />
        <span className="ml-2 inline-flex items-center gap-1 align-middle">
          {c.sourceUrl
            ? <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[11px]" title={c.sourceId}>{LIVELLO_BADGE[c.sourceType] || "⚪"}</a>
            : <span className="text-[11px]" title={c.sourceId}>{LIVELLO_BADGE[c.sourceType] || "⚪"}</span>}
          {et && <span className={`text-[9px] px-1.5 py-0.5 rounded ${et.cls}`}>{et.label}</span>}
        </span>
      </span>
    </div>
  );
}

export default function AbiAnalysis() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const [quest, setQuest] = useState(null);
  const [reportModel, setReportModel] = useState("gpt");
  const [reportBusy, setReportBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [sp, setSp] = useSearchParams();

  const loadQuest = () => {
    api.get(`/abi/analyses/${id}/questionario`).then(({ data }) => setQuest(data.esiste ? data : null)).catch(() => {});
  };
  useEffect(() => { loadQuest(); /* eslint-disable-next-line */ }, [id]);

  const creaQuestionario = async () => {
    try {
      const { data } = await api.post(`/abi/analyses/${id}/questionario`);
      setQuest(data);
      toast.success("Link questionario creato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copiaLink = (url) => {
    const full = `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(full);
    toast.success("Link copiato");
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/abi/analyses/${id}/run`);
      setD(data);
      setVersion((v) => v + 1);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const openReport = async (rigenera = false) => {
    const win = window.open("about:blank", "_blank");
    setReportBusy(true);
    try {
      const res = await api.get(`/abi/analyses/${id}/report/pdf`, { params: { model: reportModel, rigenera }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.open(url, "_blank");
      toast.success(res.headers?.["x-report-cache"] === "1" ? "Report aperto (relazione già elaborata, nessuna nuova attesa)" : "Report generato con una nuova relazione");
    } catch { if (win) win.close(); toast.error("Errore generazione report"); }
    finally { setReportBusy(false); }
  };

  useEffect(() => {
    let done = false;
    api.get(`/abi/analyses/${id}`).then(async ({ data }) => {
      setD(data);
      const aggiorna = sp.get("aggiorna") === "1";
      if (aggiorna) setSp({}, { replace: true });
      if (!done && (data.stato === "bozza" || aggiorna)) {
        setRunning(true);
        try {
          const { data: run } = await api.post(`/abi/analyses/${id}/run`);
          setD(run);
          setVersion((v) => v + 1);
        } catch { /* mostra comunque lo scheletro */ }
        finally { setRunning(false); }
      }
    }).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail)); nav("/business-intelligence");
    });
    return () => { done = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  if (!d) return <AbiShell><div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></AbiShell>;

  const title = d.denominazione || d.identificativo || d.query;

  return (
    <AbiShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="abi-analysis-page">
        <button onClick={() => nav("/business-intelligence")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="abi-back">
          <ArrowLeft className="h-4 w-4" /> Torna alle analisi
        </button>

        {/* Company snapshot */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6" data-testid="abi-snapshot">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center"><Building2 className="h-6 w-6" /></div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900">{title}</h1>
                <p className="text-sm text-slate-500">{d.tipo_input === "nome" ? "Ragione sociale" : d.tipo_input.toUpperCase()}: {d.identificativo || d.query} · Modalità <span className="capitalize">{d.mode}</span></p>
              </div>
            </div>
            <div className="text-right">
              <Badge className="bg-slate-100 text-slate-600" data-testid="abi-stato">{d.stato}</Badge>
              <div className="text-xs text-slate-400 mt-1">Completezza dossier: <b className="text-slate-700" data-testid="abi-completezza">{d.completezza?.totale ?? 0}/100</b></div>
            </div>
          </div>
          {d.nota_risoluzione && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid="abi-nota-risoluzione">{d.nota_risoluzione}</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-5">
            {SNAP_FIELDS.map(([k, label]) => (
              <div key={k} className="flex flex-col">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
                <span className="text-sm" data-testid={`abi-snap-${k}`}><Val v={fmtSnap(k, d.snapshot?.[k])} /></span>
              </div>
            ))}
          </div>
        </div>

        {/* Azioni */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={runNow} disabled={running} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="abi-run-btn">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna dati
          </Button>
          <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white pl-2" data-testid="abi-report-model-wrap">
            <span className="text-[11px] text-slate-400">Modello:</span>
            <select value={reportModel} onChange={(e) => setReportModel(e.target.value)}
              className="h-9 text-sm bg-transparent outline-none pr-1" data-testid="abi-report-model">
              <option value="gpt">GPT-5.4</option>
              <option value="claude">Claude Sonnet 4.6</option>
            </select>
            <Button variant="outline" onClick={() => openReport(false)} disabled={reportBusy} data-testid="abi-report-btn" className="border-0">
              {reportBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Genera report
            </Button>
            <Button variant="ghost" size="icon" onClick={() => openReport(true)} disabled={reportBusy} title="Rigenera la relazione AI" className="h-9 w-9" data-testid="abi-report-rigenera">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <AbiAzioni id={id} />

        {running && (
          <div className="flex items-center gap-2 mb-4 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-700" data-testid="abi-running">
            <Loader2 className="h-4 w-4 animate-spin" /> Interrogazione delle fonti in corso…
          </div>
        )}

        {/* Sezioni del dossier */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(d.sezioni || {}).map(([sid, s]) => (
            <div key={sid} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`abi-sezione-${sid}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-slate-800 text-sm">{s.label}</h3>
                <Badge variant="outline" className="text-[10px] text-slate-500">{s.stato === "compilata" ? "compilata" : "in attesa"}</Badge>
              </div>
              {(!s.campi || s.campi.length === 0) ? (
                <p className="text-xs text-slate-400 italic">Nessun dato reperito dalle fonti gratuite consultate.</p>
              ) : (
                <div className="space-y-1.5">
                  {s.campi.map((c, i) => <Campo key={i} c={c} />)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fonti consultate */}
        {(d.fonti_consultate || []).length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-5 mt-4" data-testid="abi-fonti-consultate">
            <h3 className="font-heading font-semibold text-slate-800 text-sm mb-2">Fonti consultate in questa analisi</h3>
            <div className="flex flex-wrap gap-2">
              {d.fonti_consultate.map((f) => (
                <span key={f.id} className={`text-xs px-2 py-1 rounded border ${f.esito === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`} data-testid={`abi-fonte-consultata-${f.id}`}>
                  {f.label} · {f.esito === "ok" ? `${f.n_dati} dati` : "non disponibile"}
                </span>
              ))}
            </div>
          </div>
        )}

        <AbiConfronto id={id} version={version} />

        {/* Profilo assicurativo / matrice + CAT NAT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="abi-matrice">
            <div className="flex items-center gap-2 mb-3"><ShieldAlert className="h-4 w-4 text-violet-600" /><h3 className="font-heading font-semibold text-slate-800 text-sm">Matrice delle esposizioni → prodotti</h3></div>
            {(d.rischi || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Disponibile in modalità Assicurativa dopo l'analisi.</p>
            ) : (
              <div className="space-y-2.5">
                {d.rischi.map((r, i) => (
                  <div key={i} className="border-b border-slate-50 pb-2" data-testid={`abi-rischio-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-semibold ${r.rilevanza === "alta" ? "bg-red-100 text-red-700" : r.rilevanza === "media" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.rilevanza}</span>
                      <span className="text-sm font-medium text-slate-800">{r.area}</span>
                    </div>
                    {r.descrizione && <p className="text-xs text-slate-500 mt-0.5">{r.descrizione}</p>}
                    {(r.prodotti || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {r.prodotti.map((p, j) => (
                          <span key={j} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">{p}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {d.catnat && (
              <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-5" data-testid="abi-catnat">
                <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-4 w-4 text-orange-600" /><h3 className="font-heading font-semibold text-slate-800 text-sm">Rischio catastrofale (CAT NAT)</h3></div>
                <p className="text-xs text-slate-500 mb-2">{d.catnat.comune}{d.catnat.provincia ? ` (${d.catnat.provincia})` : ""}</p>
                <div className="flex gap-2 mb-2 flex-wrap">
                  <span className="text-xs px-2 py-1 rounded bg-white border border-emerald-300" data-testid="abi-catnat-sisma">
                    Sisma: <b>{d.catnat.rischio_sismico}</b>
                    {d.catnat.sismico_fonte ? <span className="ml-1 text-[10px] text-emerald-700">✓ Verificato</span> : null}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-white border border-orange-200" data-testid="abi-catnat-alluvione">
                    Alluvione: <b>{d.catnat.rischio_alluvione}</b> <span className="ml-1 text-[10px] text-amber-700">stima</span>
                  </span>
                </div>
                {d.catnat.sismico_nota && <p className="text-[11px] text-orange-700">⚠ {d.catnat.sismico_nota}</p>}
                {d.catnat.note && <p className="text-xs text-slate-600">{d.catnat.note}</p>}
                {d.catnat.consiglio && <p className="text-xs text-orange-800 mt-1 font-medium">{d.catnat.consiglio}</p>}
                <div className="flex flex-col gap-0.5 mt-1">
                  {d.catnat.sismica_fonte_link && <a href={d.catnat.sismica_fonte_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-700 hover:underline">Classificazione sismica ufficiale (Protezione Civile) ↗</a>}
                  <a href={d.catnat.fonte_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-violet-600 hover:underline">Mappe alluvione ISPRA/IdroGEO ↗</a>
                </div>
                <p className="text-[10px] text-slate-400 mt-1 italic">Sisma: dato ufficiale per comune. Alluvione: stima indicativa da verificare sulle mappe ufficiali.</p>
              </div>
            )}
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="abi-domande">
              <div className="flex items-center gap-2 mb-2"><ListChecks className="h-4 w-4 text-violet-600" /><h3 className="font-heading font-semibold text-slate-800 text-sm">Domande da fare al cliente</h3></div>
              {(d.domande || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">Disponibile in modalità Assicurativa dopo l'analisi.</p>
              ) : (
                <ul className="space-y-1.5">
                  {d.domande.map((q, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2" data-testid={`abi-domanda-${i}`}><span className="text-violet-400">•</span>{q}</li>
                  ))}
                </ul>
              )}
              {(d.domande || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100" data-testid="abi-questionario-box">
                  {!quest ? (
                    <Button size="sm" variant="outline" onClick={creaQuestionario} data-testid="abi-questionario-crea">
                      <Link2 className="h-4 w-4 mr-1.5" /> Crea link questionario per il cliente
                    </Button>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500">Link cliente:</span>
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded truncate max-w-[240px]" data-testid="abi-questionario-link">{`${window.location.origin}${quest.url}`}</code>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copiaLink(quest.url)} data-testid="abi-questionario-copy"><Copy className="h-3.5 w-3.5" /></Button>
                      </div>
                      {quest.stato === "compilato" ? (
                        <div className="mt-3" data-testid="abi-questionario-risposte">
                          <p className="text-xs font-medium text-emerald-700 flex items-center gap-1 mb-2"><CheckCircle2 className="h-4 w-4" /> Risposte ricevute dal cliente</p>
                          <div className="space-y-2">
                            {(quest.risposte || []).map((r, i) => (
                              <div key={i} className="text-sm">
                                <p className="text-slate-500 text-xs">{r.domanda}</p>
                                <p className="text-slate-800">{r.risposta || <span className="italic text-slate-400">— nessuna risposta —</span>}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600 mt-2">In attesa di compilazione da parte del cliente…</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </AbiShell>
  );
}
